// shared/src/schemas/notification.ts
// Zod schema + enums for the `notifications` D1 table (migration 0071).
//
// See docs/ops-notices-2026-07-05-design.md. The table backs a general
// in-dashboard notification center: manual operator reminders + self-healing
// rule-derived alerts (rule rows are snooze markers only — see the migration
// header). This is the canonical Row shape; the wire-facing Notification +
// response types live in ../types/notification.ts.

import { z } from 'zod';

// ─── Enums ──────────────────────────────────────────────────────────────────
// category  — WHAT KIND of notice (drives the little tag chip + grouping).
// severity  — HOW URGENT (drives color + whether it also banners; critical
//             renders as a top banner box, warning/info live in the bell panel).
// origin    — 'manual' (operator/worker-posted, persistent) vs 'rule'
//             (computed live, self-healing; a row exists only to hold a snooze).
// status    — lifecycle for persisted rows.
export const NotificationCategoryEnum = z.enum(['reminder', 'action', 'error', 'cost', 'info']);
export const NotificationSeverityEnum = z.enum(['critical', 'warning', 'info']);
export const NotificationOriginEnum   = z.enum(['manual', 'rule']);
export const NotificationStatusEnum   = z.enum(['active', 'snoozed', 'dismissed', 'resolved']);

export type NotificationCategory = z.infer<typeof NotificationCategoryEnum>;
export type NotificationSeverity = z.infer<typeof NotificationSeverityEnum>;
export type NotificationOrigin   = z.infer<typeof NotificationOriginEnum>;
export type NotificationStatus   = z.infer<typeof NotificationStatusEnum>;

// ─── D1 row ─────────────────────────────────────────────────────────────────
// category/severity/title/body/action_* are nullable at the DB layer so a rule
// snooze marker (no displayed content of its own) is a valid row. The API layer
// enforces non-null category/severity/title for manual (origin='manual') rows.
export const NotificationRowSchema = z.object({
  id:           z.string(),
  origin:       NotificationOriginEnum,
  dedup_key:    z.string(),
  category:     NotificationCategoryEnum.nullable(),
  severity:     NotificationSeverityEnum.nullable(),
  title:        z.string().nullable(),
  body:         z.string().nullable(),
  action_url:   z.string().nullable(),
  action_label: z.string().nullable(),
  status:       NotificationStatusEnum,
  snooze_until: z.string().nullable(),
  created_at:   z.string(),
  updated_at:   z.string(),
  created_by:   z.string().nullable(),
});
export type NotificationRow = z.infer<typeof NotificationRowSchema>;
